import { container } from 'inversify.config';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { IdentityService } from 'services/identity-service';
import { AdditionalTabs, EventFormMode } from 'site/pages/events/event-form/event-form';
import {
  buildEventViewModel,
  buildInstructorsViewModel,
  EventDocumentViewState,
  EventFormStore,
  EventFormViewModel
} from 'stores/events/event-form-store';
import { EventDto, EventsStore } from 'stores/events/events-store';
import { FormTheoreticalListStore } from 'stores/events/theoretical-form-store';
import { FormPracticalListStore } from 'stores/events/practical-form-store';

import EventDetailContainer from './event-detail-container';
import EventMainData from './event-main-data';
import EventStudentContainer from './event-student-container';
import EventSupportDetailsContainer from './event-support-detail-container';
import EventTabsComponent from './event-tabs-component';
import EventInstructorContainer from './event-instructor-container';
import EventRequestsContainer from './event-requests-container';
import EventValidationContainer from './event-validation-container';
import EventDocumentationContainer from './event-documentation-container';
import EventCommnetsContainer from './event-comments-container';
import EventCheckListContainer from './event-check-list-container';
import EventFeedBackContainer from './event-feedback-container';
import EventTrainingDetailsContainer from './event-training-details-container';
import EventTheoreticalFormContainer from './event-theoretical-form-container';
import EventPracticalFormContainer from './event-practical-form-container';

import BottomToolbarActionsEvents from '../footer/eventBottomToolbar';
import TailwindCancelEventPopup from 'site/pages/events/event-form/popups/cancel-event-popup/cancel-event-popup-tw';
import ChangeStatusPopup from 'site/pages/events/event-form/popups/change-status-popup/change-status-popup';
import TailwindRequestChangesPopup from 'site/pages/events/event-form/popups/request-changes-popup/request-changes-popup-tw';
import { ItemResult } from 'stores/types';
import { ToastComponent } from '../../util/toast-component';
import TailwindMessage from '../alerts/message-tw';
interface EventContainerProp {
  onClose: (isSuccess: boolean) => void;
  onBack?: any;
  eventsStore?: EventsStore;
  fromEventTab?: boolean;
  eventFormStore?: EventFormStore;
  readOnly?: boolean;
  mode: EventFormMode;
  selectedEvent?: EventDto;
  fromRequestTab: boolean;
  isOnlySupportDetailsEnabled: boolean;
  readOnlyForPocs: boolean;
  theoreticalStore?: FormTheoreticalListStore;
  practicalStore?: FormPracticalListStore;
}

const EventContainer: React.FC<EventContainerProp> = ({
  onClose,
  onBack,
  eventsStore,
  fromEventTab,
  eventFormStore,
  readOnly = true,
  mode,
  selectedEvent,
  fromRequestTab,
  isOnlySupportDetailsEnabled,
  readOnlyForPocs,
  theoreticalStore,
  practicalStore
}) => {
  const { t } = useTranslation();
  const identityService: IdentityService = container.get(IdentityService);

  const [activeTab, setActiveTab] = useState('Event details');
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState<{ menuItem: string }[]>([]);
  const [event, setEvent] = useState<EventDto>(selectedEvent);
  const [eventFormViewModel, setEventFormViewModel] = useState<EventFormViewModel>(buildEventViewModel(selectedEvent));
  const [keepOpenedInstructorTab, setKeepOpenedInstructorTab] = useState(false);
  const [additionalTabs] = useState<AdditionalTabs>({ practical: false, theoretical: false });
  const [editMode, setEditMode] = useState(false);
  const [showCancelEventModal, setShowCancelEventModal] = useState(false);
  const [showRequestChangesModal, setShowRequestChangesModal] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const checkInstructorsTabPaneAvailability = useCallback(
    (viewModel: EventFormViewModel): boolean => {
      const { hasSupportDetails, supportDetails, hasInstructors, instructors } = viewModel;

      const hasSupportPositions: boolean =
        supportDetails && ((supportDetails.newSupportPositions || []).length > 0 || (supportDetails.supportPositions || []).length > 0);

      const eventNewModelPositions = (supportDetails.newSupportPositions || []).selectMany(x => x.eventNewModelPositions).toArray();
      const eventPositions = (supportDetails.supportPositions || []).selectMany(x => x.eventPositions).toArray();

      const hasEventPositions: boolean =
        hasSupportPositions && ((eventNewModelPositions || []).length > 0 || (eventPositions || []).length > 0);

      const newSupportPositionsWithEventNewModelPositions = (supportDetails.newSupportPositions || [])
        .filter(x => (x.eventNewModelPositions || []).length <= 0)
        .map(x => x.id);

      const supportPositionsWithEventPositions = (supportDetails.supportPositions || [])
        .filter(x => (x.eventPositions || []).length <= 0)
        .map(x => x.id);

      const mergedSupportPositions: string[] = [...newSupportPositionsWithEventNewModelPositions, ...supportPositionsWithEventPositions];

      const instructorTabPaneAvailable: boolean =
        keepOpenedInstructorTab ||
        (hasInstructors && !hasSupportDetails) ||
        (hasInstructors && hasSupportDetails && !hasEventPositions && (instructors || []).length > 0) ||
        (hasInstructors &&
          hasSupportDetails &&
          hasEventPositions &&
          (instructors || []).length > 0 &&
          ((instructors || []).some(x => x.supportPositionId == null) ||
            (instructors || []).some(x => mergedSupportPositions.includes(x.supportPositionId))));
      return instructorTabPaneAvailable;
    },
    [keepOpenedInstructorTab]
  );

  const isCheckListTabVisible = useCallback(
    (viewModel: EventFormViewModel) => {
      const currentUserInfo = identityService.getUserInfo();
      if (!viewModel.hasCheckList) {
        return false;
      }
      const existRole = viewModel.checkLists.map(e => {
        return (
          e.checkListConfig &&
          e.checkListConfig.rolesThatCreateCheckLists &&
          e.checkListConfig.rolesThatCreateCheckLists.includes(currentUserInfo.activeRole)
        );
      });
      return (viewModel.checkLists.length > 0 && existRole.some(e => e === true)) || IdentityService.isAdmin(currentUserInfo);
    },
    [identityService]
  );

  const isFeedbackTabInVisible = (viewModel: EventFormViewModel) => {
    const eventStatus = viewModel.status.toString();
    return eventStatus === 'Draft' || eventStatus === 'Planned' || !viewModel.hasFeedbackDetails;
  };

  const buildPanes = useCallback(
    (viewModel: EventFormViewModel) => {
      const { hasRequestDetails, hasEventDetails, hasSupportDetails, hasStudents, hasRequests, supportDetails } = viewModel;
      let panes: { menuItem: string }[] = [];

      if (hasRequestDetails && (!hasSupportDetails || (supportDetails && !supportDetails.isNewSupportPositionModel)))
        panes.push({
          menuItem: t('Event Details')
        });

      if (hasEventDetails)
        panes.push({
          menuItem: t('Training Details')
        });

      if (hasSupportDetails)
        panes.push({
          menuItem: t('Support Details')
        });

      if (hasStudents && (!hasSupportDetails || (supportDetails && !supportDetails.isNewSupportPositionModel)))
        panes.push({
          menuItem: t('Students')
        });

      if (checkInstructorsTabPaneAvailability(viewModel))
        panes.push({
          menuItem: t('Instructors')
        });

      if (hasRequests && !fromRequestTab)
        panes.push({
          menuItem: t('Requests')
        });

      panes.push({
        menuItem: t('Documentation')
      });

      panes.push({
        menuItem: t('Comments')
      });

      if (isCheckListTabVisible(viewModel))
        panes.push({
          menuItem: t('CheckList')
        });

      if (!isFeedbackTabInVisible(viewModel))
        panes.push({
          menuItem: t('Feedback')
        });

      if (additionalTabs.theoretical)
        panes.push({
          menuItem: t('Theoretical Form')
        });

      if (additionalTabs.practical)
        panes.push({
          menuItem: t('Practical Form')
        });
      return panes;
    },
    [additionalTabs.practical, additionalTabs.theoretical, fromRequestTab, isCheckListTabVisible, checkInstructorsTabPaneAvailability, t]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const elem = await eventsStore.getById(selectedEvent.id);
      const viewModel = buildEventViewModel(elem);
      eventFormStore.change({ ...viewModel });
      setEventFormViewModel(viewModel);
      setEvent(elem);
      setEditMode(false); // reset a solo lectura al cargar
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [eventsStore, eventFormStore, selectedEvent.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (eventFormViewModel) {
      const panes = buildPanes(eventFormViewModel);
      const first = (panes || [])[0]?.menuItem;
      setActiveTab(first);
      setTabs(panes);
    }
  }, [buildPanes, additionalTabs, eventFormViewModel]);

  // --- Handlers Toolbar ---
  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleSaveClick = async () => {
    eventFormStore.change({ ...eventFormStore.state.item });

    setLoading(true);
    try {
      const result = await eventFormStore.updateEvent();
      if (result?.isSuccess) {
        const docs = ((result as ItemResult<EventDto>).item.supportingDocuments || []).map(d => ({
          ...d,
          path: null,
          state: EventDocumentViewState.Unmodified
        }));

        const instructorsVM = buildInstructorsViewModel((result as ItemResult<EventDto>).item.instructors || []);
        // Actualizar store solo una vez con datos importantes
        eventFormStore.change({ supportingDocuments: docs, instructors: instructorsVM });

        ToastComponent({ text: t('Form successfully saved!'), type: 'success-toast' });

        setEditMode(false);
        await load();
      } else {
        const messages = eventFormStore.state.result?.messages;
        messages?.forEach(msg => ToastComponent({ text: msg.body, type: 'warning-toast' }));
      }
    } catch (err) {
      console.error(err);
      ToastComponent({ text: t('An unexpected error occurred.'), type: 'error-toast' });
    } finally {
      setLoading(false);
    }
    console.log('handleSaveClick pulsado');
  };

  const handleOnDataChange = (data: Partial<EventFormViewModel>) => {
    if (!eventFormViewModel) return;
    const copy = { ...eventFormViewModel, ...data };
    setEventFormViewModel(copy);
    eventFormStore?.change(copy);
  };

  // const canBeCancelled = (): boolean => {
  //   const eventData = eventFormViewModel;
  //   if (!eventData) return false;
  //   return eventData.status.toString() !== 'Completed' && eventData.status.toString() !== 'Closed';
  // };

  const handleOnEventCancelled = async () => {
    setShowCancelEventModal(false);
    await load();
    onClose?.(true);
  };

  const handleOnCancelEventClick = () => {
    setShowCancelEventModal(true);
  };

  const handleOnNextStatusClicked = async () => {
    await eventFormStore.updateEvent();

    const result = eventFormStore.state.result;
    if (result == null || result.isSuccess) {
      await load();
      setShowChangeStatusModal(true);
    } else {
      // Mostrar todos los mensajes de error en el toast
      (result?.messages || []).forEach(msg => ToastComponent({ text: msg.body, type: 'warning-toast' }));
    }
  };

  const handleOnAcceptNextStatus = async () => {
    setShowChangeStatusModal(false);
    await eventFormStore.nextStatus();
    await load(); // Opcional: recarga los datos después de cambiar el estado.
  };

  // const isNextStatusButtonVisible = (): boolean => {
  //   const eventItem = eventFormStore.state.item;
  //   if (!hasAdministrationRightsOrIsEventStatusPlannedInProgressAndCurrentUserIsInstructor(eventItem)) {
  //     return false;
  //   }
  //   const nextStatus = getNextStatus(eventItem);
  //   return nextStatus && nextStatus.toString() !== 'Closed';
  // };

  // Handlers extra para rest of toolbar
  const handleOnPreviousStatus = async () => {
    await eventFormStore.previousStatus();
    // Opcional: recargar datos
    await load();
  };

  const handleOnRequestChanges = () => {
    setShowRequestChangesModal(true);
  };

  const handleOnValidate = async () => {
    await eventFormStore.validateStatus();
    await load();
  };

  const handleOnCloseEvent = async () => {
    await load();
  };

  const handleOnCancelMergePreview = () => {
    onBack?.();
    setEditMode(false);
  };

  const handleOnMerge = async () => {
    if (eventFormStore.createMergeRequest) {
      await eventFormStore.createMergeRequest();
      await load();
    } else {
      console.warn('No existe createMergeRequest en eventFormStore');
    }
  };

  // const handleOnCreate = async () => {
  //   await eventFormStore.createEvent();
  //   // Redirigir o actualizar lista
  //   onClose(true);
  // };

  // ==== RENDER ====

  return (
    <div className="min-h-screen p-4 rounded-md shadow-md relative max-w-4xl mx-auto" style={{ paddingBottom: '120px' }}>
      {/* Botón de volver */}
      <button
        onClick={() => {
          onBack?.();
          setEditMode(false);
        }}
        className="flex items-center gap-1 text-sm mb-2"
      >
        ← Back
      </button>

      {loading || !event ? (
        // Spinner de carga
        <div className="flex items-center justify-center p-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2">{t('Loading event...')}</span>
        </div>
      ) : (
        <>
          {eventFormStore?.state &&
            eventFormStore.state?.result &&
            eventFormStore.state.result?.messages &&
            eventFormStore.state.result?.messages.length > 0 && (
              <TailwindMessage
                error
                icon="exclamation-circle"
                header={t('An error ocurred')}
                list={eventFormStore.state.result?.messages.map(o => o.body).filter(x => x !== '')}
                className="mb-4"
              />
            )}

          {/* Datos principales del evento */}
          <EventMainData selectedItem={eventFormViewModel} readOnly={!editMode} mode={mode} onChange={handleOnDataChange} />

          <div className="h-px w-full bg-gray-200 my-4" />

          {/* Componente de tabs */}
          <EventTabsComponent activeTab={activeTab} tabs={tabs} onEventClick={setActiveTab} />

          {/* Contenido según la pestaña activa */}
          <div className="min-h-[150px]">
            {activeTab === 'Event Details' && (
              <EventDetailContainer
                event={eventFormViewModel}
                mode={mode}
                readOnly={isOnlySupportDetailsEnabled || !editMode}
                loading={false}
              />
            )}
            {activeTab === 'Training Details' && (
              <EventTrainingDetailsContainer
                event={eventFormViewModel}
                readOnly={isOnlySupportDetailsEnabled || !editMode}
                loading={false}
              />
            )}
            {activeTab === 'Support Details' && (
              <EventSupportDetailsContainer
                event={eventFormViewModel}
                readOnly={!editMode}
                isHideEditButton={isOnlySupportDetailsEnabled}
              />
            )}
            {activeTab === 'Students' && <EventStudentContainer event={eventFormViewModel} readOnly={readOnlyForPocs && !editMode} />}
            {activeTab === 'Instructors' && (
              <EventInstructorContainer
                selectedEventFormViewModel={eventFormViewModel}
                onClose={() => onClose?.(false)}
                onKeepOpen={() => setKeepOpenedInstructorTab(true)}
                isTabReadonly={isOnlySupportDetailsEnabled || !editMode}
                readOnly={!editMode}
                selectedEvent={selectedEvent}
              />
            )}
            {activeTab === 'Requests' && (
              <EventRequestsContainer
                selectedEventFormViewModel={eventFormViewModel}
                readOnly={isOnlySupportDetailsEnabled || !editMode}
                fromEventTab
              />
            )}
            {activeTab === 'Validations' && <EventValidationContainer event={eventFormViewModel} />}
            {activeTab === 'Documentation' && (
              <EventDocumentationContainer selectedEventFormViewModel={eventFormViewModel} readOnly={!editMode} />
            )}
            {activeTab === 'Comments' && (
              <EventCommnetsContainer
                selectedEventFormViewModel={eventFormViewModel}
                readOnly={isOnlySupportDetailsEnabled || !editMode}
                loading={false}
              />
            )}
            {activeTab === 'CheckList' && (
              <EventCheckListContainer
                selectedEventFormViewModel={eventFormViewModel}
                readOnly={isOnlySupportDetailsEnabled || !editMode}
              />
            )}
            {activeTab === 'Feedback' && <EventFeedBackContainer selectedEventFormViewModel={eventFormViewModel} />}
            {activeTab === 'Theoretical Form' && <EventTheoreticalFormContainer selectedEventFormViewModel={eventFormViewModel} />}
            {activeTab === 'Practical Form' && <EventPracticalFormContainer selectedEventFormViewModel={eventFormViewModel} />}
          </div>
        </>
      )}
      {/* Toolbar con lógica y visibilidad dinámica */}
      {!fromRequestTab && (
        <BottomToolbarActionsEvents
          eventFormStore={eventFormStore}
          identityService={identityService}
          mode={mode}
          readOnly={!editMode}
          readOnlyForPocs={readOnlyForPocs}
          sidebarWidthPx={56}
          onPreviousStatus={handleOnPreviousStatus}
          onRequestChanges={handleOnRequestChanges}
          onCancelEvent={handleOnCancelEventClick}
          onEdit={handleEditClick}
          onSave={handleSaveClick}
          onValidate={handleOnValidate}
          onNextStatus={handleOnNextStatusClicked}
          onCloseEvent={handleOnCloseEvent}
          onCancel={handleOnCancelMergePreview}
          onMerge={handleOnMerge}
          // onCreate={handleOnCreate}
        />
      )}

      {/* Popup Cancelar Evento */}
      {showCancelEventModal && (
        <TailwindCancelEventPopup
          open={showCancelEventModal}
          onSuccess={handleOnEventCancelled}
          onCancel={() => setShowCancelEventModal(false)}
          onError={() => alert('Error cancelando el evento')}
          cancelRequest={false}
        />
      )}

      {/* Popup Solicitar Cambios */}
      {showRequestChangesModal && (
        <TailwindRequestChangesPopup
          open={showRequestChangesModal}
          eventId={eventFormViewModel?.id}
          onSendClicked={() => {
            setShowRequestChangesModal(false);
            load(); // refrescar datos tras enviar
          }}
          onCancelClicked={() => setShowRequestChangesModal(false)}
        />
      )}

      {/* Popup Cambio de Estado */}
      {showChangeStatusModal && (
        <ChangeStatusPopup
          eventFormStore={eventFormStore}
          open={showChangeStatusModal}
          onAcceptClicked={handleOnAcceptNextStatus}
          onCancelClicked={() => setShowChangeStatusModal(false)}
        />
      )}
    </div>
  );
};

export default connect(
  ['theoreticalStore', FormTheoreticalListStore],
  ['practicalStore', FormPracticalListStore],
  ['eventsStore', EventsStore],
  ['eventFormStore', EventFormStore]
)(EventContainer);
